'use client';
import { createContext, useContext, useState } from 'react';
import { CopilotPreviewProvider } from './CopilotPreviewProvider';
import { generateId } from 'ai';
import { Message, PartialThread, StreamBuffer } from '@/lib/copilot/message';
import { EmittableMessageData, Thread } from '@/lib/copilot/types';
import { toObject } from '@/lib/copilot/emittable';
import { useFeatureFlags } from './FeatureFlagsProvider';
import { sendGTMEvent } from '@next/third-parties/google';

type ChatTools = {
  messages: Message[];
  thread: Thread;
  status: 'ready' | 'in_progress' | 'error';
  input: string | null;
  error?: Error;
  addMessage: (message: Message) => void;
  setInput: (input: string | null) => void;
  submitMessage: (event?: React.FormEvent<HTMLFormElement>) => Promise<void>;
  retry: () => void;
};

const ChatContext = createContext<ChatTools>(null!);

export const useChat = () => useContext(ChatContext);

export const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const [thread, setThread] = useState<PartialThread>(new PartialThread());
  const [input, setInput] = useState<ChatTools['input']>(null);
  const [status, setStatus] = useState<ChatTools['status']>('ready');
  const { flags } = useFeatureFlags();

  const addMessage = (message: Message) => {
    // Create a new thread instance to avoid mutating the existing one
    const newThread = new PartialThread([...thread.messages, message]);
    setThread(newThread);

    if (message.role === 'user') {
      sendGTMEvent({
        event: 'copilotUserMessage',
        value: 1,
      })
      setStatus('in_progress');
      const buffer = new StreamBuffer();
      fetch('/api/agent/' + flags.COPILOT_ID, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ thread: newThread.toData() }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          if (!response.body) {
            throw new Error('Response body is null');
          }

          const reader: ReadableStreamDefaultReader<Uint8Array> = response.body.getReader();
          const decoder: TextDecoder = new TextDecoder('utf-8');

          function readStream(i: number) {
            reader.read().then(({ done, value }) => {
              if (done) {
                setStatus('ready');
                return;
              }

              const text = decoder.decode(value, { stream: true });
              buffer.add(text);

              // Update thread immutably using the latest state
              setThread((prevThread) => {
                const updatedThread = new PartialThread(prevThread.messages);
                buffer.flush().forEach((messages: EmittableMessageData[]) => {
                  messages.forEach((message: EmittableMessageData) => {
                    toObject(message).attach(updatedThread)
                  });
                });
                return updatedThread;
              });

              readStream(i + 1);
            });
          }

          readStream(0);
        })
        .catch((error) => {
          console.error(error);
          setStatus('error');
        });
    }
  };

  const retry = () => {
    let lastMessage: Message | undefined;
    do {
      lastMessage = thread.messages.pop();
      if (lastMessage?.role === 'user') {
        break;
      }
    }
    while (lastMessage);

    if (lastMessage) {
      setThread(new PartialThread(thread.messages));
      addMessage(lastMessage);
    }
  }

  const submitMessage = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault?.();

    if (input === '' || input === null) {
      return;
    }
    addMessage(
      new Message({
        id: generateId(),
        role: 'user',
        content: input,
        status: 'completed',
      })
    );
    setInput(null);
  };

  return (
    <ChatContext.Provider
      value={{
        status,
        thread,
        messages: thread.messages,
        input,
        addMessage,
        submitMessage,
        setInput,
        retry
      }}
    >
      <CopilotPreviewProvider>{children}</CopilotPreviewProvider>
    </ChatContext.Provider>
  );
};